import React, { useMemo, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import api from '../../services/api';

// React 19 compatibility: react-quill previously relied on removed ReactDOM.findDOMNode
// Override getEditingArea to use refs directly instead of findDOMNode
const AnyReactQuill: any = ReactQuill;
if (
  AnyReactQuill &&
  AnyReactQuill.prototype &&
  !AnyReactQuill.prototype.__patchedNoFindDomNode
) {
  AnyReactQuill.prototype.getEditingArea = function getEditingAreaPatched() {
    const editor = this.editingArea;
    if (!editor) return null;
    if (editor instanceof Element) return editor;
    if (editor && 'current' in editor) {
      return editor.current;
    }
    return null;
  };
  AnyReactQuill.prototype.__patchedNoFindDomNode = true;
}

type ContentRichTextEditorProps = {
  label: string;
  value: string;
  onChange: (html: string) => void;
  helperText?: string;
};

const ContentRichTextEditor: React.FC<ContentRichTextEditorProps> = ({
  label,
  value,
  onChange,
  helperText = 'Supports subscript/superscript, alignment, lists, links, and image upload.',
}) => {
  const quillRef = useRef<ReactQuill | null>(null);

  const handleImageUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const formData = new FormData();
      formData.append('file', file);
      try {
        const res = await api.post<{ url?: string; path?: string }>(
          '/uploads/editor',
          formData,
          { timeout: 60000 }
        );
        const rawPath = res.data.path || '';
        let url = res.data.url || rawPath;
        if (rawPath && (!url || !/^https?:\/\//.test(url))) {
          const base = (api.defaults.baseURL as string | undefined) || '';
          const origin = base.startsWith('http')
            ? new URL(base).origin
            : window.location.origin;
          url = new URL(rawPath, origin).toString();
        }
        const editor = quillRef.current?.getEditor();
        if (!editor || !url) return;
        const range = editor.getSelection(true);
        const index = range ? range.index : editor.getLength();
        editor.insertEmbed(index, 'image', url, 'user');
        editor.setSelection(index + 1, 0, 'user');
      } catch (err) {
        // eslint-disable-next-line no-alert
        alert('Image upload failed. Please try again later.');
      }
    };
    input.click();
  };

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [2, 3, false] }],
          ['bold', 'italic', 'underline'],
          [{ script: 'sub' }, { script: 'super' }],
          [{ align: [] }],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['link', 'image'],
          ['clean'],
        ],
        handlers: {
          image: handleImageUpload,
        },
      },
    }),
    []
  );

  const formats = [
    'header',
    'bold',
    'italic',
    'underline',
    'script',
    'align',
    'list',
    'bullet',
    'link',
    'image',
  ];

  return (
    <div>
      <label className="mb-1 block font-medium text-neutral-700">{label}</label>
      <div className="rounded-md border border-neutral-300 bg-white">
        <ReactQuill
          ref={quillRef}
          className="inline-richtext-editor"
          theme="snow"
          value={value}
          onChange={onChange}
          modules={modules}
          formats={formats}
        />
      </div>
      <p className="mt-1 text-[10px] text-neutral-500">{helperText}</p>
    </div>
  );
};

export default ContentRichTextEditor;
